import { router } from '@/router';
import { api_endpoints, type EndpointKey } from './endpoints';
import { KeyValueStore } from '@/utils/keyvalue-store';
import { i18n } from '@/i18n';

type ApiResponse<T, E> = { ok: true; raw: Response; data: T } | { ok: false; raw: Response; data?: E };

type ApiMiddleware<T extends 'request' | 'response'> = T extends 'request'
  ? { type: 'request'; handler: (input: Request) => Request | null }
  : { type: 'response'; handler: (input: Response) => Response | null };

type ApiRequestOptions = {
  endpointName: EndpointKey;
  url: string;
  options: RequestInit;
};

function NewApiRequest<ResponseType, ErrorType, RequestType = Record<string, any>>(
  options: ApiRequestOptions,
  middleware?: ApiMiddleware<'request' | 'response'>[]
): ApiRequest<ResponseType, ErrorType, RequestType> {
  return new ApiRequest<ResponseType, ErrorType, RequestType>(options, middleware);
}

export class ApiRequest<ResponseType, ErrorType, RequestType = Record<string, any>> {
  private endpointName: EndpointKey;
  private url: string;
  private options: RequestInit;
  private middleware: ApiMiddleware<'request' | 'response'>[];

  constructor({ endpointName, url, options }: ApiRequestOptions, middleware?: ApiMiddleware<'request' | 'response'>[]) {
    this.url = url;
    this.options = options;
    this.endpointName = endpointName;
    this.middleware = middleware || [];
  }

  get endpoint(): (typeof api_endpoints)[EndpointKey] {
    return api_endpoints[this.endpointName];
  }

  applyMiddleware(r: Request | Response | null): Request | Response | null {
    let modified = r;

    if (!r) {
      return null; // If the request or response is null, return null immediately
    }

    const type = 'status' in r ? 'response' : 'request';

    for (const mw of this.middleware) {
      if (!modified) {
        console.warn(`Middleware skipped because the ${type} is null`);
        continue;
      }

      if (mw.type === type) {
        modified = mw.handler(modified as any) as Request | Response | null;
      }
    }

    return modified;
  }

  async fetch(): Promise<ApiResponse<ResponseType, ErrorType>> {
    try {
      const request = this.applyMiddleware(new Request(this.url, this.options)) as Request;
      if (!request) {
        throw new Error('Request middleware did not return a valid Request object');
      }
      const response = this.applyMiddleware(await fetch(request)) as Response;
      if (!response) {
        throw new Error('Response middleware did not return a valid Response object');
      }

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorType;
        return { ok: false, raw: response, data: errorData };
      }

      return { ok: true, raw: response, data: (await response.json()) as ResponseType };
    } catch (error) {
      console.error('Network or parsing error:', error);
      throw new Error('An unexpected error occurred. Please try again later.');
    }
  }

  // Adds path parameters to the URL
  withParams(...pairs: string[]): ApiRequest<ResponseType, ErrorType, RequestType> {
    const params = new KeyValueStore(pairs);

    let url = this.url;
    params.forEach((key, value) => {
      if (this.endpoint.params?.includes(key)) {
        url = url.replace(`:${key}`, encodeURIComponent(value));
      } else {
        console.warn(`Parameter ${key} is not defined in the endpoint ${this.endpointName}`);
      }
    });

    // Check if all required parameters are provided
    this.endpoint.params?.forEach((param: any) => {
      if (url.includes(`:${param}`) && !(param in pairs!)) {
        throw new Error(`Missing required parameter: ${param}`);
      }
    });

    return NewApiRequest<ResponseType, ErrorType, RequestType>(
      {
        endpointName: this.endpointName,
        url,
        options: this.options,
      },
      this.middleware
    );
  }

  withData(data: RequestType): ApiRequest<ResponseType, ErrorType, RequestType> {
    const options = { ...this.options, body: JSON.stringify(data) };
    return NewApiRequest<ResponseType, ErrorType, RequestType>(
      {
        endpointName: this.endpointName,
        url: this.url,
        options,
      },
      this.middleware
    );
  }

  withNormalizedData(data: Record<string, any>): ApiRequest<ResponseType, ErrorType, RequestType> {
    const normalizedData: Record<string, any> = {};

    for (const [key, value] of Object.entries(data)) {
      if (value === undefined || value === '') {
        continue;
      }

      if (key.includes('.')) {
        const parts = key.split('.');
        let current = normalizedData;

        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!(part in current)) {
            current[part] = {};
          }
          current = current[part];
        }

        current[parts[parts.length - 1]] = value;
      } else {
        normalizedData[key] = value;
      }
    }

    return this.withData(normalizedData as RequestType);
  }

  // Adds query parameters to the URL (This should not be called multiple times on the same request)
  withQuery(...pairs: (string | number)[]): ApiRequest<ResponseType, ErrorType, RequestType> {
    const query = new KeyValueStore(pairs);
    const queryParams = new URLSearchParams(this.url.split('?')[1] || '');
    query.forEach((key, value) => {
      if (queryParams.has(key)) {
        queryParams.set(key, String(value)); // Update existing parameter
      } else {
        queryParams.append(key, String(value)); // Add new parameter
      }
    });

    const urlWithQuery = `${this.url.split('?')[0]}?${queryParams.toString()}`;
    return NewApiRequest<ResponseType, ErrorType, RequestType>(
      {
        endpointName: this.endpointName,
        url: urlWithQuery,
        options: this.options,
      },
      this.middleware
    );
  }
}

class Api {
  private baseUrl: string;
  private middleware: ApiMiddleware<'request' | 'response'>[] = [];

  constructor(baseUrl: string, ...middleware: ApiMiddleware<'request' | 'response'>[]) {
    this.baseUrl = baseUrl;
    this.middleware = middleware;
  }

  request<K extends EndpointKey>(
    endpointName: K
  ): ApiRequest<(typeof api_endpoints)[K]['types']['response'], (typeof api_endpoints)[K]['types']['error'], (typeof api_endpoints)[K]['types']['request']> {
    const endpoint = api_endpoints[endpointName];
    let url = `${this.baseUrl}${endpoint.url}`;
    const options: RequestInit = {
      method: endpoint.method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    return NewApiRequest<
      (typeof api_endpoints)[K]['types']['response'],
      (typeof api_endpoints)[K]['types']['error'],
      (typeof api_endpoints)[K]['types']['request']
    >(
      {
        endpointName,
        url,
        options,
      },
      this.middleware
    );
  }
}

const api = new Api(
  import.meta.env.VITE_API_URL,
  // Authorization middleware
  {
    type: 'request',
    handler: (req: Request) => {
      const token = localStorage.getItem('nh-token');
      if (token) {
        // Pre-pend 'Bearer' if it is not already present
        req.headers.set('Authorization', token.startsWith('Bearer ') ? token : `Bearer ${token}`);
      }
      return req;
    },
  },
  // Language middleware – mirrors the active i18n locale to the backend Localizer middleware
  {
    type: 'request',
    handler: (req: Request) => {
      req.headers.set('Accept-Language', i18n.global.locale.value);
      return req;
    },
  },
  {
    type: 'response',
    handler: (res: Response) => {
      // Grab token from headers
      const token = res.headers.get('Authorization');
      if (token) {
        // Store token in localStorage or Vuex store
        localStorage.setItem('nh-token', token);
      }

      // If the 'Authorization' header is present, but empty, clear the localStorage token and log the user out
      if (token === '') {
        localStorage.removeItem('nh-token');
        console.debug('Token cleared from localStorage');
        router.push({
          name: 'Auth',
          query: { message: 'You have been logged out. Please log in again.' },
        });
      }

      return res;
    },
  }
);
export default api;
